import { SearchInput, Text } from '@billy/design-system'
import { ChangeEvent, ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BodyContainer, EmptyState, FilterOptionRow, OptionList } from '../../styles'

interface MultiSelectFilterBodyProps {
  options: string[]
  selectedValues: string[]
  searchPlaceholder: string
  noResultsLabel: string
  onChange: (values: string[]) => void
}

export const MultiSelectFilterBody = ({
  options,
  selectedValues,
  searchPlaceholder,
  noResultsLabel,
  onChange,
}: MultiSelectFilterBodyProps): ReactElement => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const filteredOptions = useMemo(() => {
    const needle = search.trim().toLowerCase()

    if (!needle) {
      return options
    }

    return options.filter((option) => option.toLowerCase().includes(needle))
  }, [options, search])

  const toggleOption = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter((value) => value !== option))
      return
    }

    onChange([...selectedValues, option])
  }

  return (
    <BodyContainer>
      <SearchInput
        fullWidth
        onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
        placeholder={searchPlaceholder}
        value={search}
      />
      {filteredOptions.length === 0 ? (
        <EmptyState>
          <Text variant="bodyMD">{noResultsLabel || t('review_entries.filters.no_results')}</Text>
        </EmptyState>
      ) : (
        <OptionList>
          {filteredOptions.map((option) => (
            <FilterOptionRow
              checked={selectedValues.includes(option)}
              key={option}
              onChange={() => toggleOption(option)}
              size="l"
              value={option}
            >
              {option}
            </FilterOptionRow>
          ))}
        </OptionList>
      )}
    </BodyContainer>
  )
}
